import type { TicketEntryLogListParams } from "./types";
import type { WorkspaceId } from "./utils/workspaceStorage";

/** Central TanStack Query keys for cache alignment and invalidation. */
export const queryKeys = {
  events: {
    all: (workspace: WorkspaceId) => ["events", workspace] as const,
    list: (workspace: WorkspaceId) => [...queryKeys.events.all(workspace), "list"] as const,
    analytics: (workspace: WorkspaceId) => [...queryKeys.events.all(workspace), "analytics"] as const,
    detail: (workspace: WorkspaceId, eventId: string) => [...queryKeys.events.all(workspace), "detail", eventId] as const,
    dashboard: (workspace: WorkspaceId, eventId: string) => [...queryKeys.events.all(workspace), "dashboard", eventId] as const,
    /** Sell-through curve backing the P&L scenario slider. */
    forecastCurve: (workspace: WorkspaceId, eventId: string) =>
      [...queryKeys.events.all(workspace), "forecast-curve", eventId] as const,
  },
  eventOs: {
    all: (workspace: WorkspaceId) => ["eventOs", workspace] as const,
    /** Stable key when no event is selected (query disabled). */
    workspaceIdle: (workspace: WorkspaceId) => ["eventOs", workspace, "workspace", "idle"] as const,
    workspace: (workspace: WorkspaceId, eventId: string) => [...queryKeys.eventOs.all(workspace), "workspace", eventId] as const,
  },
  artists: {
    all: (workspace: WorkspaceId) => ["artists", workspace] as const,
    list: (workspace: WorkspaceId) => ["artists", workspace, "list"] as const,
  },
  contacts: {
    all: (workspace: WorkspaceId) => ["contacts", workspace] as const,
    list: (workspace: WorkspaceId, search?: string) => ["contacts", workspace, "list", search ?? ""] as const,
    detail: (workspace: WorkspaceId, contactId: string) => ["contacts", workspace, "detail", contactId] as const,
  },
  venues: {
    all: (workspace: WorkspaceId) => ["venues", workspace] as const,
    list: (workspace: WorkspaceId) => ["venues", workspace, "list"] as const,
  },
  companyMembers: {
    all: (workspace: WorkspaceId) => ["companyMembers", workspace] as const,
    list: (workspace: WorkspaceId) => ["companyMembers", workspace, "list"] as const,
  },
  budgets: (workspace: WorkspaceId, eventId: string) => ["budgets", workspace, eventId] as const,
  tasks: (workspace: WorkspaceId, eventId: string) => ["tasks", workspace, eventId] as const,
  schedules: (workspace: WorkspaceId, eventId: string) => ["schedules", workspace, eventId] as const,
  invoices: (workspace: WorkspaceId, eventId: string) => ["invoices", workspace, eventId] as const,
  /** Manage Invoices tab: server-side search/sort, so params are part of the key. */
  invoiceSearch: (
    workspace: WorkspaceId,
    eventId: string,
    params: { q?: string; sort?: string }
  ) => ["invoices", workspace, eventId, "search", params.q ?? "", params.sort ?? "date"] as const,
  rsvpGuests: (workspace: WorkspaceId, eventId: string) => ["rsvp", workspace, eventId, "guests"] as const,
  rsvpSettings: (workspace: WorkspaceId, eventId: string) => ["rsvp", workspace, eventId, "settings"] as const,
  walkInGuests: (workspace: WorkspaceId, eventId: string) => ["walkins", workspace, eventId, "guests"] as const,
  promoterGuests: (workspace: WorkspaceId) => ["guests", workspace, "promoter"] as const,
  eventPromoters: (workspace: WorkspaceId, eventId: string) =>
    ["promoters", workspace, eventId] as const,
  publicRsvp: (token: string) => ["rsvp", "public", token] as const,
  publicGuestList: (token: string) => ["guest-list", "public", token] as const,
  publicWalkIn: (token: string) => ["walk-in", "public", token] as const,
  inviteForm: (eventId: string) => ["invites", eventId, "form"] as const,
  inviteRequests: (eventId: string) => ["invites", eventId, "requests"] as const,
  publicInvite: (token: string) => ["invites", "public", token] as const,
  publicInvitePay: (payToken: string) => ["invites", "pay", payToken] as const,
  ticketEntries: (eventId: string, params?: TicketEntryLogListParams) =>
    ["ticketEntries", eventId, params ?? {}] as const,
  ticketScanStats: (eventId: string) => ["ticketScan", eventId, "stats"] as const,
  activityAnalytics: (eventId: string) => ["activityAnalytics", eventId] as const,
  rsvpGuestSearch: (eventId: string, q: string) => ["rsvp", eventId, "guestSearch", q] as const,
  teamMembers: () => ["teamMembers"] as const,
  companyOperatorAccounts: () => ["companyOperatorAccounts"] as const,
  eventPromoterAccounts: (eventId: string) => ["eventPromoterAccounts", eventId] as const,
  ticketingPartners: () => ["ticketingPartners"] as const,
  eventNotes: (workspace: WorkspaceId, eventId: string) => ["eventNotes", workspace, eventId] as const,
  eventDealTerms: (workspace: WorkspaceId, eventId: string) => ["eventDealTerms", workspace, eventId] as const,
  eventFiles: (workspace: WorkspaceId, eventId: string) => ["eventFiles", workspace, eventId] as const,
};
